import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { schoolsAPI, teachersAPI, studentsAPI, coachesAPI, programsAPI, locationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  Loader2,
  School,
  Users,
  GraduationCap,
  Phone,
  MapPin,
  User,
  BookOpen,
  Calendar,
  Award,
} from 'lucide-react';
import { toast } from 'sonner';

const SchoolDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, canEdit } = useAuth();
  const [school, setSchool] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students');
  
  // Teacher dialog
  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherForm, setTeacherForm] = useState({
    name: '',
    contact: '',
    classes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Student dialog (Add/Edit)
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);
  const [selectedStudentForEdit, setSelectedStudentForEdit] = useState(null);
  const [studentForm, setStudentForm] = useState({
    first_name: '',
    last_name: '',
    sex: '',
    date_of_birth: '',
    grade: '',
    guardian_name: '',
    guardian_relationship: '',
    guardian_primary_phone: '',
    guardian_secondary_phone: '',
    guardian_email: '',
    program_id: '',
    location_id: '',
    assigned_coach_id: '',
    skill_level: 'Beginner',
  });

  // Student detail modal
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [schoolRes, coachesRes, programsRes, locationsRes] = await Promise.all([
        schoolsAPI.getById(id),
        coachesAPI.getAll({}),
        programsAPI.getAll(),
        locationsAPI.getAll({}),
      ]);
      setSchool(schoolRes.data);
      setCoaches(coachesRes.data);
      setPrograms(programsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load school data');
      navigate('/schools');
    } finally {
      setLoading(false);
    }
  };

  // Student CRUD handlers
  const resetStudentForm = () => {
    setStudentForm({
      first_name: '',
      last_name: '',
      sex: '',
      date_of_birth: '',
      grade: '',
      guardian_name: '',
      guardian_relationship: '',
      guardian_primary_phone: '',
      guardian_secondary_phone: '',
      guardian_email: '',
      program_id: programs[0]?.id || '',
      location_id: locations[0]?.id || '',
      assigned_coach_id: '',
      skill_level: 'Beginner',
    });
    setSelectedStudentForEdit(null);
  };

  const openAddStudentDialog = () => {
    resetStudentForm();
    setStudentDialogOpen(true);
  };

  const openEditStudentDialog = (student) => {
    setSelectedStudentForEdit(student);
    setStudentForm({
      first_name: student.first_name || student.name?.split(' ')[0] || '',
      last_name: student.last_name || student.name?.split(' ').slice(1).join(' ') || '',
      sex: student.sex || '',
      date_of_birth: student.date_of_birth || '',
      grade: student.grade || '',
      guardian_name: student.guardian_name || '',
      guardian_relationship: student.guardian_relationship || '',
      guardian_primary_phone: student.guardian_primary_phone || student.guardian_phone || '',
      guardian_secondary_phone: student.guardian_secondary_phone || '',
      guardian_email: student.guardian_email || '',
      program_id: student.program_id || '',
      location_id: student.location_id || '',
      assigned_coach_id: student.assigned_coach_id || '',
      skill_level: student.skill_level || 'Beginner',
    });
    setStudentDialogOpen(true);
  };

  const handleStudentSubmit = async (e) => {
    e.preventDefault();
    if (!studentForm.first_name || !studentForm.last_name) {
      toast.error('First name and last name are required');
      return;
    }
    if (!studentForm.program_id || !studentForm.location_id) {
      toast.error('Program and location are required');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        ...studentForm,
        school_id: id,
      };

      if (selectedStudentForEdit) {
        await studentsAPI.update(selectedStudentForEdit.id, data);
        toast.success('Student updated');
      } else {
        await studentsAPI.create(data);
        toast.success('Student added to school');
      }
      setStudentDialogOpen(false);
      resetStudentForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save student');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to remove this student?')) return;
    try {
      await studentsAPI.delete(studentId);
      toast.success('Student removed');
      loadData();
    } catch (error) {
      toast.error('Failed to delete student');
    }
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    if (!teacherForm.name) {
      toast.error('Teacher name is required');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        name: teacherForm.name,
        contact: teacherForm.contact,
        classes: teacherForm.classes ? teacherForm.classes.split(',').map(c => c.trim()) : [],
        school_id: id,
      };

      if (selectedTeacher) {
        await teachersAPI.update(selectedTeacher.id, data);
        toast.success('Teacher updated');
      } else {
        await teachersAPI.create(data);
        toast.success('Teacher added');
      }
      setTeacherDialogOpen(false);
      resetTeacherForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save teacher');
    } finally {
      setSubmitting(false);
    }
  };

  const resetTeacherForm = () => {
    setTeacherForm({ name: '', contact: '', classes: '' });
    setSelectedTeacher(null);
  };

  const openEditTeacher = (teacher) => {
    setSelectedTeacher(teacher);
    setTeacherForm({
      name: teacher.name,
      contact: teacher.contact || '',
      classes: teacher.classes?.join(', ') || '',
    });
    setTeacherDialogOpen(true);
  };

  const handleDeleteTeacher = async (teacherId) => {
    if (!window.confirm('Delete this teacher?')) return;
    try {
      await teachersAPI.delete(teacherId);
      toast.success('Teacher deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete teacher');
    }
  };

  const openStudentModal = (student) => {
    setSelectedStudent(student);
    setStudentModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!school) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/schools')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold text-slate-900">{school.name}</h1>
          {school.address && (
            <p className="text-slate-500 flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {school.address}
            </p>
          )}
        </div>
        {isAdmin() && (
          <Button variant="outline" onClick={() => navigate(`/schools`)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit School
          </Button>
        )}
      </div>

      {/* School Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Principal</p>
                <p className="font-medium">{school.principal_name || 'Not assigned'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <Phone className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Contact</p>
                <p className="font-medium">{school.principal_contact || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Students</p>
                <p className="font-medium">{school.student_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <GraduationCap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Teachers</p>
                <p className="font-medium">{school.teacher_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
          <TabsTrigger value="students" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Students ({school.students?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="teachers" className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4" />
            Teachers ({school.teachers?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Students Tab */}
        <TabsContent value="students">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Students</CardTitle>
              {canEdit() && (
                <Button onClick={openAddStudentDialog} data-testid="add-student-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Student
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="sticky left-0 bg-slate-50">Name</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>Age</TableHead>
                      <TableHead>Sex</TableHead>
                      <TableHead>Parent</TableHead>
                      <TableHead>Parent Contact</TableHead>
                      <TableHead className="text-center">Lessons</TableHead>
                      <TableHead>Program</TableHead>
                      <TableHead>Coach</TableHead>
                      {canEdit() && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!school.students?.length ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-slate-400">
                          No students enrolled from this school
                        </TableCell>
                      </TableRow>
                    ) : (
                      school.students.map((student) => (
                        <TableRow
                          key={student.id}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => openStudentModal(student)}
                          data-testid={`student-row-${student.id}`}
                        >
                          <TableCell className="sticky left-0 bg-white font-medium">
                            {student.name || `${student.first_name} ${student.last_name}`}
                          </TableCell>
                          <TableCell>{student.grade || '-'}</TableCell>
                          <TableCell>{student.age}</TableCell>
                          <TableCell className="capitalize">{student.sex || '-'}</TableCell>
                          <TableCell>{student.guardian_name || '-'}</TableCell>
                          <TableCell>{student.guardian_primary_phone || student.guardian_phone || '-'}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{student.lessons_attended || student.lessons_completed || 0}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{student.program_name || '-'}</Badge>
                          </TableCell>
                          <TableCell>{student.coach_name || '-'}</TableCell>
                          {canEdit() && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button size="sm" variant="ghost" onClick={() => openEditStudentDialog(student)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteStudent(student.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Teachers Tab */}
        <TabsContent value="teachers">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Teachers</CardTitle>
              {canEdit() && (
                <Button onClick={() => { resetTeacherForm(); setTeacherDialogOpen(true); }} data-testid="add-teacher-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Teacher
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Classes</TableHead>
                      {canEdit() && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!school.teachers?.length ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                          No teachers added
                        </TableCell>
                      </TableRow>
                    ) : (
                      school.teachers.map((teacher) => (
                        <TableRow key={teacher.id} data-testid={`teacher-row-${teacher.id}`}>
                          <TableCell className="font-medium">{teacher.name}</TableCell>
                          <TableCell>{teacher.contact || '-'}</TableCell>
                          <TableCell>
                            {teacher.classes?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {teacher.classes.map((cls, idx) => (
                                  <Badge key={idx} variant="outline">{cls}</Badge>
                                ))}
                              </div>
                            ) : '-'}
                          </TableCell>
                          {canEdit() && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button size="sm" variant="ghost" onClick={() => openEditTeacher(teacher)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500"
                                  onClick={() => handleDeleteTeacher(teacher.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Teacher Dialog */}
      <Dialog open={teacherDialogOpen} onOpenChange={(open) => { setTeacherDialogOpen(open); if (!open) resetTeacherForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedTeacher ? 'Edit Teacher' : 'Add Teacher'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddTeacher} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={teacherForm.name}
                onChange={(e) => setTeacherForm({ ...teacherForm, name: e.target.value })}
                placeholder="Teacher name"
                data-testid="teacher-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact</Label>
              <Input
                value={teacherForm.contact}
                onChange={(e) => setTeacherForm({ ...teacherForm, contact: e.target.value })}
                placeholder="Phone or email"
              />
            </div>
            <div className="space-y-2">
              <Label>Classes (comma-separated)</Label>
              <Input
                value={teacherForm.classes}
                onChange={(e) => setTeacherForm({ ...teacherForm, classes: e.target.value })}
                placeholder="Grade 1, Grade 2, etc."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTeacherDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-teacher-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedTeacher ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Student Detail Modal */}
      <Dialog open={studentModalOpen} onOpenChange={setStudentModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Student Details</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl">
                  {selectedStudent.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedStudent.name}</h3>
                  <p className="text-slate-500">Age: {selectedStudent.age} | {selectedStudent.sex || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Grade</p>
                  <p className="font-medium">{selectedStudent.grade || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Program</p>
                  <Badge>{selectedStudent.program_name || '-'}</Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Parent/Guardian</p>
                  <p className="font-medium">{selectedStudent.guardian_name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Parent Contact</p>
                  <p className="font-medium">{selectedStudent.guardian_phone || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Coach</p>
                  <p className="font-medium">{selectedStudent.coach_name || 'Not assigned'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Lessons Attended</p>
                  <p className="font-medium">{selectedStudent.lessons_attended || selectedStudent.lessons_completed || 0}</p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button
                  className="w-full"
                  onClick={() => {
                    setStudentModalOpen(false);
                    navigate(`/students/${selectedStudent.id}`);
                  }}
                >
                  View Full Profile
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Student Dialog */}
      <Dialog open={studentDialogOpen} onOpenChange={(open) => { setStudentDialogOpen(open); if (!open) resetStudentForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedStudentForEdit ? 'Edit Student' : 'Add New Student'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleStudentSubmit} className="space-y-4">
            {/* Personal Info */}
            <div className="space-y-3">
              <h4 className="font-medium text-slate-900">Personal Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>First Name *</Label>
                  <Input
                    value={studentForm.first_name}
                    onChange={(e) => setStudentForm({ ...studentForm, first_name: e.target.value })}
                    placeholder="First name"
                    data-testid="student-first-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Last Name *</Label>
                  <Input
                    value={studentForm.last_name}
                    onChange={(e) => setStudentForm({ ...studentForm, last_name: e.target.value })}
                    placeholder="Last name"
                    data-testid="student-last-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Sex</Label>
                  <Select 
                    value={studentForm.sex || undefined} 
                    onValueChange={(v) => setStudentForm({ ...studentForm, sex: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={studentForm.date_of_birth}
                    onChange={(e) => setStudentForm({ ...studentForm, date_of_birth: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Grade/Class</Label>
                  <Input
                    value={studentForm.grade}
                    onChange={(e) => setStudentForm({ ...studentForm, grade: e.target.value })}
                    placeholder="e.g., Grade 3"
                  />
                </div>
              </div>
            </div>

            {/* Parent/Guardian Info */}
            <div className="space-y-3 pt-3 border-t">
              <h4 className="font-medium text-slate-900">Parent/Guardian Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Full Name</Label>
                  <Input
                    value={studentForm.guardian_name}
                    onChange={(e) => setStudentForm({ ...studentForm, guardian_name: e.target.value })}
                    placeholder="Parent/Guardian name"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Relationship</Label>
                  <Select 
                    value={studentForm.guardian_relationship || undefined} 
                    onValueChange={(v) => setStudentForm({ ...studentForm, guardian_relationship: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mother">Mother</SelectItem>
                      <SelectItem value="Father">Father</SelectItem>
                      <SelectItem value="Guardian">Guardian</SelectItem>
                      <SelectItem value="Grandparent">Grandparent</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Primary Phone</Label>
                  <Input
                    value={studentForm.guardian_primary_phone}
                    onChange={(e) => setStudentForm({ ...studentForm, guardian_primary_phone: e.target.value })}
                    placeholder="+268 7XXX XXXX"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Secondary Phone</Label>
                  <Input
                    value={studentForm.guardian_secondary_phone}
                    onChange={(e) => setStudentForm({ ...studentForm, guardian_secondary_phone: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={studentForm.guardian_email}
                  onChange={(e) => setStudentForm({ ...studentForm, guardian_email: e.target.value })}
                  placeholder="parent@email.com"
                />
              </div>
            </div>

            {/* Program Info */}
            <div className="space-y-3 pt-3 border-t">
              <h4 className="font-medium text-slate-900">Program Assignment</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Program *</Label>
                  <Select 
                    value={studentForm.program_id || undefined} 
                    onValueChange={(v) => setStudentForm({ ...studentForm, program_id: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select program" /></SelectTrigger>
                    <SelectContent>
                      {programs.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Location *</Label>
                  <Select 
                    value={studentForm.location_id || undefined} 
                    onValueChange={(v) => setStudentForm({ ...studentForm, location_id: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Assigned Coach</Label>
                  <Select 
                    value={studentForm.assigned_coach_id || "none"} 
                    onValueChange={(v) => setStudentForm({ ...studentForm, assigned_coach_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select coach" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No coach assigned</SelectItem>
                      {coaches.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Skill Level</Label>
                  <Select 
                    value={studentForm.skill_level || "Beginner"} 
                    onValueChange={(v) => setStudentForm({ ...studentForm, skill_level: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Beginner">Beginner</SelectItem>
                      <SelectItem value="Intermediate">Intermediate</SelectItem>
                      <SelectItem value="Advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStudentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-student-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedStudentForEdit ? 'Update' : 'Add Student'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchoolDetail;
